<template>
  <div v-if="doctors.length > 0" class="doctors-filter-bar">
    <div class="doctors-filter-bar__label">
      <v-icon size="16" color="#546e7a">mdi-stethoscope</v-icon>
      <span>Médicos</span>
    </div>

    <div class="doctors-filter-bar__actions">
      <button
        type="button"
        class="doctors-filter-bar__action-btn"
        :class="{ 'is-disabled': allSelected }"
        :disabled="allSelected"
        @click="selectAll"
      >
        Todos
      </button>
      <span class="doctors-filter-bar__sep">·</span>
      <button
        type="button"
        class="doctors-filter-bar__action-btn"
        :class="{ 'is-disabled': noneSelected }"
        :disabled="noneSelected"
        @click="clearAll"
      >
        Limpar
      </button>
    </div>

    <div class="doctors-filter-bar__scroll" ref="scroll">
      <button
        v-for="doctor in doctors"
        :key="doctor.id"
        type="button"
        class="doctor-chip"
        :class="{ 'doctor-chip--active': isSelected(doctor.id) }"
        :style="chipStyle(doctor)"
        :title="doctor.name"
        @click="toggle(doctor.id)"
      >
        <span class="doctor-chip__dot" :style="{ background: doctorColor(doctor) }" />
        <span class="doctor-chip__name">{{ shortName(doctor.name) }}</span>
        <v-icon
          v-if="isSelected(doctor.id)"
          size="14"
          class="doctor-chip__check"
          color="currentColor"
        >
          mdi-check
        </v-icon>
      </button>
    </div>

    <div class="doctors-filter-bar__count">
      {{ value.length }}/{{ doctors.length }}
    </div>
  </div>
</template>

<script>
export default {
  name: "DoctorsFilterBar",

  props: {
    doctors: {
      type: Array,
      required: true,
      // [{ id, name, color?, profile_image? }]
    },
    value: {
      type: Array,
      required: true,
      // ids selecionados
    },
  },

  computed: {
    allSelected() {
      return this.value.length === this.doctors.length && this.doctors.length > 0;
    },
    noneSelected() {
      return this.value.length === 0;
    },
  },

  methods: {
    isSelected(id) {
      return this.value.includes(id);
    },

    toggle(id) {
      const next = this.isSelected(id)
        ? this.value.filter((v) => v !== id)
        : [...this.value, id];
      this.$emit("input", next);
    },

    selectAll() {
      this.$emit("input", this.doctors.map((d) => d.id));
    },

    clearAll() {
      this.$emit("input", []);
    },

    shortName(fullName) {
      if (!fullName) return "—";
      const parts = fullName.trim().split(/\s+/);
      if (parts.length === 1) return parts[0];
      // "Dr. João Silva" → "Dr. João S."
      const first = parts.slice(0, 2).join(" ");
      const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase();
      return parts.length > 2 ? `${first} ${lastInitial}.` : fullName;
    },

    doctorColor(doctor) {
      if (doctor.color) return doctor.color;
      return this.hashColor(doctor.name || String(doctor.id));
    },

    hashColor(seed) {
      // Hash determinístico → matiz HSL estável e agradável
      let hash = 0;
      for (let i = 0; i < seed.length; i++) {
        hash = seed.charCodeAt(i) + ((hash << 5) - hash);
        hash |= 0;
      }
      const hue = Math.abs(hash) % 360;
      return `hsl(${hue}, 55%, 48%)`;
    },

    chipStyle(doctor) {
      if (!this.isSelected(doctor.id)) return {};
      const color = this.doctorColor(doctor);
      return {
        background: this.tint(color, 0.12),
        borderColor: color,
        color,
      };
    },

    tint(color, alpha) {
      // hsl(...) → hsla(..., alpha) | #hex → rgba()
      if (color.startsWith("hsl(")) {
        return color.replace("hsl(", "hsla(").replace(")", `, ${alpha})`);
      }
      if (color.startsWith("#")) {
        const hex = color.replace("#", "");
        const bigint = parseInt(
          hex.length === 3
            ? hex
                .split("")
                .map((c) => c + c)
                .join("")
            : hex,
          16,
        );
        const r = (bigint >> 16) & 255;
        const g = (bigint >> 8) & 255;
        const b = bigint & 255;
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      }
      return color;
    },
  },
};
</script>

<style scoped>
.doctors-filter-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  background: #fafbfc;
  border: 1px solid var(--ds-blgrey-50);
  border-radius: 12px;
  margin-bottom: 8px;
  flex-shrink: 0;
}

.doctors-filter-bar__label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: var(--ds-text-550);
  text-transform: uppercase;
  letter-spacing: 0.4px;
  white-space: nowrap;
}

.doctors-filter-bar__actions {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.doctors-filter-bar__action-btn {
  font-size: 12px;
  font-weight: 600;
  color: var(--ds-primary);
  background: transparent;
  border: none;
  padding: 2px 4px;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.12s ease;
}

.doctors-filter-bar__action-btn:hover:not(.is-disabled) {
  background-color: var(--ds-blue-100);
}

.doctors-filter-bar__action-btn.is-disabled {
  color: #b0bec5;
  cursor: default;
}

.doctors-filter-bar__sep {
  color: var(--ds-blgrey-100);
  font-size: 12px;
}

.doctors-filter-bar__scroll {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 2px 0;
  scrollbar-width: thin;
}

.doctors-filter-bar__scroll::-webkit-scrollbar {
  height: 4px;
}

.doctors-filter-bar__scroll::-webkit-scrollbar-thumb {
  background: var(--ds-blgrey-100);
  border-radius: 2px;
}

.doctors-filter-bar__count {
  font-size: 11px;
  font-weight: 600;
  color: var(--ds-text-soft);
  white-space: nowrap;
  padding: 4px 8px;
  background: var(--ds-blgrey-50);
  border-radius: 10px;
}

/* ═══ Chip individual ═══ */
.doctor-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  font-size: 12.5px;
  font-weight: 500;
  border: 1.5px solid var(--ds-blgrey-100);
  border-radius: 16px;
  background: var(--ds-surface-white);
  color: var(--ds-text-soft);
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.12s ease, border-color 0.12s ease,
    color 0.12s ease, transform 0.12s ease;
  font-family: inherit;
}

.doctor-chip:hover:not(.doctor-chip--active) {
  border-color: var(--ds-text-muted);
  color: var(--ds-text-700);
  background: var(--ds-blue-bg);
}

.doctor-chip--active {
  font-weight: 600;
}

.doctor-chip__dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  box-shadow: 0 0 0 1.5px var(--ds-white) inset;
}

.doctor-chip__name {
  line-height: 1;
}

.doctor-chip__check {
  margin-left: -2px;
}
</style>
